'use client'

import dynamic from 'next/dynamic'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useState } from 'react'

import { ContactFormSkeleton } from '@/components/ContactForm/ContactFormSkeleton'
import { Drawer } from '@/components/Drawer'
import { usePrefetchOnFirstInteraction } from '@/hooks/usePrefetchOnFirstInteraction'

import type { ExtendedFormType } from '@/blocks/Form/Component'

const loadContactForm = () => import('@/components/ContactForm/Form')

const ContactFormInner = dynamic(loadContactForm, {
  ssr: false,
  loading: () => <ContactFormSkeleton />,
})

/**
 * Defers the contact drawer contents (FormBlock and friends) out of the
 * shared layout bundle. The chunk is prefetched on first interaction. The
 * drawer shell mounts here and animates in exactly once per open; while the
 * chunk downloads, the drawer shows the skeleton, then the real form swaps in
 * place inside it.
 */
const ContactFormLazy = ({ form }: { form: ExtendedFormType }) => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const open = searchParams.get('contact-open') !== null

  // Stay mounted after the first open so the close animation can play and
  // re-opens are instant.
  const [hasOpened, setHasOpened] = useState(false)
  if (open && !hasOpened) setHasOpened(true)

  usePrefetchOnFirstInteraction(loadContactForm)

  if (!hasOpened) return null

  return (
    <Drawer
      open={open}
      setOpen={(isOpen) => {
        // Remove the query parameter when the drawer closes
        if (!isOpen) router.replace(pathname)
      }}
      ariaTitle="Contact Us"
    >
      <ContactFormInner form={form} />
    </Drawer>
  )
}

export default ContactFormLazy
